import type {
    ButtonHTMLAttributes,
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import {
    createContext,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react'

import { createPortal } from 'react-dom'
import { ChevronDown, CircleCheck, LoaderCircle, TriangleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useScrollLock } from '@/hooks/use-scroll-lock'

// Small, consistent building blocks for the admin dashboard.

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: 'sm' | 'md' | 'lg'
    loading?: boolean
}

export const buttonClass = (variant: ButtonVariant = 'primary', size: 'sm' | 'md' | 'lg' = 'md') => cn(
    'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-caramel-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
    size === 'sm' && 'h-9 px-3.5 text-sm',
    size === 'md' && 'h-11 px-5 text-sm',
    size === 'lg' && 'h-12 px-6 text-[15px]',
    variant === 'primary' && 'bg-cocoa-800 text-cream-50 shadow-[0_10px_24px_-14px_rgba(58,40,30,0.8)] hover:bg-cocoa-700',
    variant === 'secondary' && 'border border-border bg-white text-cocoa-800 hover:border-cocoa-700/30 hover:bg-cream-50',
    variant === 'ghost' && 'text-cocoa-700 hover:bg-cream-100',
    variant === 'soft' && 'bg-blush-100 text-blush-600 hover:bg-blush-200',
    variant === 'danger' && 'bg-[#b4473f] text-white hover:bg-[#9c3b34]'
)

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ variant = 'primary', size = 'md', loading = false, className, children, disabled, type = 'button', ...props }, ref) => (
    <button ref={ref} type={type} disabled={disabled || loading} className={cn(buttonClass(variant, size), className)} {...props}>
        {loading && <LoaderCircle className='size-4 animate-spin' />}
        {children}
    </button>
))

Button.displayName = 'Button'

const fieldBase = 'w-full rounded-2xl border border-input bg-white px-4 text-[15px] text-cocoa-900 outline-none transition placeholder:text-cocoa-500/60 focus:border-caramel-400 focus:ring-4 focus:ring-caramel-300/25 disabled:bg-cream-100 aria-[invalid=true]:border-[#c4544b]'

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
    <input ref={ref} className={cn(fieldBase, 'h-12', className)} {...props} />
))

Input.displayName = 'Input'

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(({ className, ...props }, ref) => (
    <textarea ref={ref} className={cn(fieldBase, 'min-h-28 resize-y py-3 leading-relaxed', className)} {...props} />
))

Textarea.displayName = 'Textarea'

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(({ className, children, ...props }, ref) => (
    <span className='relative block'>
        <select ref={ref} className={cn(fieldBase, 'h-12 appearance-none pr-11', className)} {...props}>
            {children}
        </select>
        <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-cocoa-500' />
    </span>
))

Select.displayName = 'Select'

interface FieldProps {
    label: string
    htmlFor?: string
    hint?: ReactNode
    error?: string | null
    optional?: boolean
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({ label, htmlFor, hint, error, optional = false, className, children }): ReactNode => (
    <div className={cn('space-y-2', className)}>
        <label htmlFor={htmlFor} className='block text-sm font-semibold text-cocoa-800'>
            {label}
            {optional && <span className='font-normal text-muted-foreground'> (opsional)</span>}
        </label>
        {children}
        {error
            ? <p role='alert' className='text-xs font-semibold text-[#b4473f]'>{error}</p>
            : hint && <p className='text-xs leading-relaxed text-muted-foreground'>{hint}</p>}
    </div>
)

interface SwitchProps {
    checked: boolean
    onChange: (value: boolean) => void
    label: string
    description?: string
    disabled?: boolean
    id?: string
}

export const Switch: FC<SwitchProps> = ({ checked, onChange, label, description, disabled = false, id }): ReactNode => (
    <label htmlFor={id} className={cn('flex cursor-pointer items-start justify-between gap-4', disabled && 'cursor-not-allowed opacity-60')}>
        <span>
            <span className='block text-sm font-semibold text-cocoa-800'>{label}</span>
            {description && <span className='mt-0.5 block text-xs leading-relaxed text-muted-foreground'>{description}</span>}
        </span>
        <span className='relative mt-0.5 inline-flex shrink-0'>
            <input
                id={id}
                type='checkbox'
                role='switch'
                checked={checked}
                disabled={disabled}
                onChange={event => onChange(event.target.checked)}
                className='peer sr-only'
            />
            <span className='h-7 w-12 rounded-full bg-cocoa-500/30 transition peer-checked:bg-[#5f8a55] peer-focus-visible:ring-2 peer-focus-visible:ring-caramel-400 peer-focus-visible:ring-offset-2' />
            <span className='absolute left-1 top-1 size-5 rounded-full bg-white shadow transition peer-checked:translate-x-5' />
        </span>
    </label>
)

export const Card: FC<{ className?: string, children: ReactNode, title?: ReactNode, description?: ReactNode, action?: ReactNode }> = ({ className, children, title, description, action }): ReactNode => (
    <section className={cn('rounded-[28px] border border-border/80 bg-white p-5 shadow-[0_18px_40px_-34px_rgba(58,40,30,0.45)] sm:p-6', className)}>
        {(title || action) && (
            <div className='mb-5 flex flex-wrap items-start justify-between gap-3'>
                <div>
                    {title && <h2 className='font-heading text-xl text-cocoa-900'>{title}</h2>}
                    {description && <p className='mt-1 text-sm text-muted-foreground'>{description}</p>}
                </div>
                {action}
            </div>
        )}
        {children}
    </section>
)

export const Pill: FC<{ tone?: 'neutral' | 'green' | 'pink' | 'brown' | 'amber' | 'red', className?: string, children: ReactNode }> = ({ tone = 'neutral', className, children }): ReactNode => (
    <span className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold',
        tone === 'neutral' && 'bg-cream-100 text-cocoa-700',
        tone === 'green' && 'bg-[#e4eedf] text-[#3f6638]',
        tone === 'pink' && 'bg-blush-100 text-blush-600',
        tone === 'brown' && 'bg-cocoa-800 text-cream-50',
        tone === 'amber' && 'bg-[#fbeccb] text-[#8a5a12]',
        tone === 'red' && 'bg-[#f8e0dc] text-[#9c3b34]',
        className
    )}>
        {children}
    </span>
)

// ---------------------------------------------------------------------------
// Confirm dialog
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
    open: boolean
    title: string
    description: ReactNode
    confirmLabel: string
    tone?: 'danger' | 'primary'
    loading?: boolean
    onConfirm: () => void
    onCancel: () => void
}

export const ConfirmDialog: FC<ConfirmDialogProps> = ({ open, title, description, confirmLabel, tone = 'danger', loading = false, onConfirm, onCancel }): ReactNode => {
    const [mounted, setMounted] = useState(false)
    const cancelRef = useRef<HTMLButtonElement>(null)

    useEffect(() => setMounted(true), [])
    useScrollLock(open)

    useEffect(() => {
        if (!open) return

        cancelRef.current?.focus()

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !loading) onCancel()
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open, loading, onCancel])

    if (!mounted || !open) return null

    return createPortal(
        <div className='fixed inset-0 z-[120] flex items-end justify-center p-4 sm:items-center'>
            <div aria-hidden='true' onClick={loading ? undefined : onCancel} className='absolute inset-0 bg-cocoa-900/45 backdrop-blur-[2px] animate-in fade-in' />
            <div role='alertdialog' aria-modal='true' aria-labelledby='confirm-title' className='relative w-full max-w-md rounded-[28px] bg-cream-50 p-6 shadow-[0_30px_80px_-30px_rgba(42,29,22,0.6)] animate-in fade-in zoom-in-95 duration-200'>
                <div className='flex items-start gap-4'>
                    <span className={cn('grid size-11 shrink-0 place-items-center rounded-full', tone === 'danger' ? 'bg-[#f8e0dc] text-[#9c3b34]' : 'bg-blush-100 text-blush-600')}>
                        <TriangleAlert className='size-5' />
                    </span>
                    <div>
                        <h2 id='confirm-title' className='font-heading text-xl text-cocoa-900'>{title}</h2>
                        <div className='mt-2 text-sm leading-relaxed text-muted-foreground'>{description}</div>
                    </div>
                </div>
                <div className='mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
                    <Button ref={cancelRef} variant='secondary' onClick={onCancel} disabled={loading}>
                        Batal
                    </Button>
                    <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
                        {confirmLabel}
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    )
}

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

type Toast = { id: number, tone: 'success' | 'error', message: string }

const ToastContext = createContext<(tone: Toast['tone'], message: string) => void>(() => undefined)

export const AdminToastProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [toasts, setToasts] = useState<Array<Toast>>([])

    const push = useCallback((tone: Toast['tone'], message: string) => {
        const id = Date.now() + Math.random()

        setToasts(current => [...current.slice(-2), { id, tone, message }])
        window.setTimeout(() => setToasts(current => current.filter(toast => toast.id !== id)), tone === 'error' ? 6000 : 3500)
    }, [])

    return (
        <ToastContext.Provider value={push}>
            {children}
            <div aria-live='polite' className='pointer-events-none fixed inset-x-0 bottom-4 z-[130] flex flex-col items-center gap-2 px-4 sm:bottom-6 sm:items-end sm:px-6'>
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        role={toast.tone === 'error' ? 'alert' : 'status'}
                        className='pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-3xl border border-white/70 bg-cream-50/95 p-3.5 shadow-[0_24px_60px_-24px_rgba(58,40,30,0.55)] backdrop-blur-xl animate-in fade-in slide-in-from-bottom-2'
                    >
                        <span className={cn('grid size-8 shrink-0 place-items-center rounded-full', toast.tone === 'success' ? 'bg-[#e4eedf] text-[#3f6638]' : 'bg-[#f8e0dc] text-[#9c3b34]')}>
                            {toast.tone === 'success' ? <CircleCheck className='size-4' /> : <TriangleAlert className='size-4' />}
                        </span>
                        <p className='flex-1 pt-1 text-sm font-semibold text-cocoa-900'>{toast.message}</p>
                        <button
                            type='button'
                            onClick={() => setToasts(current => current.filter(item => item.id !== toast.id))}
                            aria-label='Tutup'
                            className='grid size-7 shrink-0 place-items-center rounded-full text-cocoa-500 hover:bg-muted'
                        >
                            <X className='size-4' />
                        </button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    )
}

export const useAdminToast = () => useContext(ToastContext)
